import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../firebase/UserProvider";
import { getAllLabels, listTaskUsers, getTaskCountsPerLabel, updateLabel, deleteLabel } from "../../firebase/tasks";
import { LabelTag } from "../../components/tms/LabelTag";
import TaskSearchInput from "../../components/tms/TaskSearchInput";

export function LabelsReport() {
  const { user } = useSession();
  const [labels, setLabels] = useState([]);
  const [users, setUsers] = useState([]);
  const [taskCounts, setTaskCounts] = useState(new Map());
  const [tempColors, setTempColors] = useState({});
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLabelId, setActionLabelId] = useState(null); // tracking row edits/deletes
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const isAdmin = user?.email === "admin@bmspune.org";

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      setError(null);
      try {
        const [labelsList, usersList, counts] = await Promise.all([
          getAllLabels(),
          listTaskUsers(),
          getTaskCountsPerLabel(),
        ]);
        setLabels(labelsList);
        setUsers(usersList);
        setTaskCounts(counts);
      } catch (err) {
        setError("Failed to load report data.");
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const getCreatorName = (createdBy) => {
    if (!createdBy) return "System";
    const matched = users.find((u) => u.uid === createdBy);
    return matched ? matched.name : createdBy;
  };

  const handleColorChange = (labelId, newColor) => {
    setTempColors((prev) => ({ ...prev, [labelId]: newColor }));
  };

  const handleColorSave = async (labelId, labelName, color) => {
    setActionLabelId(labelId);
    try {
      await updateLabel(labelId, { color });
      setLabels((prev) =>
        prev.map((l) => (l.id === labelId ? { ...l, color } : l))
      );
    } catch (err) {
      setError(`Failed to update color for label "${labelName}".`);
    } finally {
      setActionLabelId(null);
    }
  };

  const handleDelete = async (labelId, labelName) => {
    setActionLabelId(labelId);
    try {
      await deleteLabel(labelId, labelName);
      setLabels((prev) => prev.filter((l) => l.id !== labelId));
      setConfirmDeleteId(null);
    } catch (err) {
      setError(`Failed to delete label "${labelName}".`);
    } finally {
      setActionLabelId(null);
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "—";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="container-fluid min-h-screen py-8 px-4 md:px-8" style={{ backgroundColor: "var(--theme-color-canvas)" }}>
      {/* Header */}
      <div className="mb-8 pb-5 flex flex-col md:flex-row justify-between items-start md:items-center" style={{ borderBottom: "1px solid var(--theme-color-mute)" }}>
        <div>
          <div className="flex flex-wrap items-center gap-4">
            <h1 className="text-3xl font-extrabold tracking-tight m-0" style={{ color: "var(--theme-color-ink)", fontFamily: "var(--theme-font-family-display)" }}>
              Report: Labels Report
            </h1>
            <TaskSearchInput />
          </div>
          <p className="mt-1 text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>
            View and manage all labels, check usage statistics, customize colors, and manage tags.
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <Link
            to="/tasks/dashboard"
            className="inline-flex items-center justify-center font-semibold py-2 px-4 rounded-lg border transition-colors text-sm no-underline"
            style={{
              backgroundColor: "var(--theme-color-canvas-soft)",
              color: "var(--theme-color-body)",
              borderColor: "var(--theme-color-mute)",
            }}
          >
            ← Admin Dashboard
          </Link>
        </div>
      </div>

      {error && (
        <div className="p-4 mb-6 text-sm rounded-xl border font-semibold text-rose-700 bg-rose-50 border-rose-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border shadow-sm p-8 animate-pulse text-center" style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}>
          <div className="h-6 bg-slate-200 rounded w-1/4 mx-auto mb-4"></div>
          <div className="h-4 bg-slate-200 rounded w-1/2 mx-auto"></div>
        </div>
      ) : labels.length === 0 ? (
        <div className="rounded-2xl border shadow-sm py-16 px-4 text-center max-w-xl mx-auto" style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}>
          <h3 className="text-lg font-bold m-0" style={{ color: "var(--theme-color-ink)" }}>No Labels Found</h3>
          <p className="mt-2 text-sm leading-relaxed max-w-sm mx-auto" style={{ color: "var(--theme-color-body)" }}>
            There are currently no labels registered in the system.
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border shadow-sm overflow-hidden" style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead style={{ backgroundColor: "var(--theme-color-canvas-soft)", borderBottom: "1px solid var(--theme-color-mute)" }}>
                <tr className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--theme-color-body-mid)" }}>
                  <th className="p-4 pl-6">Label Name</th>
                  <th className="p-4 w-40">Label Color</th>
                  <th className="p-4">Created By</th>
                  <th className="p-4">Created At</th>
                  <th className="p-4 w-36">No. of Tasks</th>
                  {isAdmin && <th className="p-4 w-44">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {labels.map((label) => {
                  const nameKey = label.name.toLowerCase().trim();
                  const count = taskCounts.get(nameKey) || 0;
                  const currentColor = tempColors[label.id] ?? label.color ?? "#6b7280";
                  const isActionPending = actionLabelId === label.id;

                  return (
                    <tr
                      key={label.id}
                      className="transition-colors"
                      style={{ borderBottom: "1px solid var(--theme-color-canvas-soft)" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--theme-color-canvas-soft)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      <td className="p-4 pl-6 align-middle">
                        <LabelTag name={label.name} color={currentColor} />
                      </td>
                      <td className="p-4 align-middle">
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={currentColor}
                            onChange={(e) => handleColorChange(label.id, e.target.value)}
                            onBlur={(e) => handleColorSave(label.id, label.name, e.target.value)}
                            disabled={isActionPending}
                            className="w-8 h-8 rounded cursor-pointer border border-slate-300 p-0"
                            title="Choose Color"
                          />
                          <span className="text-xs font-mono text-slate-500 uppercase">{currentColor}</span>
                        </div>
                      </td>
                      <td className="p-4 align-middle text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>
                        {getCreatorName(label.createdBy)}
                      </td>
                      <td className="p-4 align-middle text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>
                        {formatDateTime(label.createdAt)}
                      </td>
                      <td className="p-4 align-middle text-sm font-semibold">
                        <span className="bg-slate-100 text-slate-700 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-slate-200">
                          {count}
                        </span>
                      </td>
                      {isAdmin && (
                        <td className="p-4 align-middle text-sm">
                          {confirmDeleteId === label.id ? (
                            <div className="flex items-center gap-2">
                              <span className="text-rose-600 font-medium text-xs">Confirm?</span>
                              <button
                                onClick={() => handleDelete(label.id, label.name)}
                                disabled={isActionPending}
                                className="px-2 py-1 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 rounded border-none cursor-pointer"
                              >
                                Yes
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(null)}
                                disabled={isActionPending}
                                className="px-2 py-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded border border-slate-300 cursor-pointer"
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmDeleteId(label.id)}
                              disabled={isActionPending}
                              className="px-3 py-1.5 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded transition-colors cursor-pointer"
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
